import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { adminPipelineAPI } from './pipeline';

//admin API'S

// Shared cross-slice invalidation: a freshly-added lead / created
// application appears on both the Loan Information leads page (this
// slice's 'updateLoanInfo' tag, handled via invalidatesTags) AND on the
// Pipeline page (adminPipelineAPI's 'updateAdminPipeline'). RTK Query
// tag invalidation doesn't cross slices, so the pipeline poke has to
// happen explicitly here.
const _invalidateAdminPipelineOnSuccess = async (
  _arg,
  { dispatch, queryFulfilled }
) => {
  try {
    await queryFulfilled;
    dispatch(adminPipelineAPI.util.invalidateTags(['updateAdminPipeline']));
  } catch {
    // Creation failed; pipeline cache stays valid.
  }
};

export const loanInformationAPI = createApi({
  reducerPath: 'loanInformation',
  tagTypes: ['updateLoanInfo', 'dashboardData'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    // add leads
    addLeads: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/lead`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateAdminPipelineOnSuccess,
      invalidatesTags: ['updateLoanInfo'],
    }),
    createConsumerApplication: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/application/new`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateAdminPipelineOnSuccess,
      invalidatesTags: ['updateLoanInfo'],
    }),
    //get loan officer list
    getLoanOfficers: builder.query({
      query: () => {
        return {
          url: `/b2b/user/loan_officers/`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    //get leads table
    getAllLeads: builder.query({
      query: ({ data, paging, searchFilter }) => {
        return {
          url: `/b2b/loan/loa/lead_info?page_size=${paging?.limit}&page=${paging?.offset}&search=${searchFilter}`,
          method: 'POST',
          headers: getHeaders(),
          body: data,
        };
      },
      providesTags: ['updateLoanInfo'],
    }),
    //get leads detail
    getLeadDetails: builder.query({
      query: ({ user_id, application_id }) => {
        return {
          url: `/b2b/loan/dashboard/view_lead_details?user_id=${user_id}&application_id=${application_id}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    getDashboardInformation: builder.query({
      query: () => {
        return {
          url: `b2b/loan/loa/dashboard_info`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    getDashboardSummary: builder.query({
      query: () => ({
        url: `b2b/loan/dashboard/summary`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['dashboardData'],
    }),
    getDashboardNewLeads: builder.query({
      query: ({ page = 1, page_size = 10 } = {}) => ({
        url: `b2b/loan/dashboard/new_leads?page=${page}&page_size=${page_size}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['dashboardData'],
    }),
    getDashboardPipeline: builder.query({
      query: ({ page = 1, page_size = 10 } = {}) => ({
        url: `b2b/loan/dashboard/pipeline?page=${page}&page_size=${page_size}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['dashboardData'],
    }),
    getDashboardLenderSpread: builder.query({
      query: () => ({
        url: `b2b/loan/dashboard/lender-spread`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data || null,
    }),
    // The loan processor's own queue, one round trip for the whole dashboard.
    // Scoped server-side to the signed-in processor, so it takes no arguments.
    getProcessorDashboard: builder.query({
      query: () => ({
        url: `b2b/loan/dashboard/processor`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data || null,
      providesTags: ['dashboardData'],
    }),
    // The underwriter's desk. Scoped to the signed-in underwriter for what is
    // theirs, wider for the unclaimed files and the pending circumstances.
    getUnderwriterDashboard: builder.query({
      query: () => ({
        url: `b2b/loan/dashboard/underwriter`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data || null,
      providesTags: ['dashboardData'],
    }),
    // The officer's book: leads and pipeline together, which no other role
    // dashboard spans.
    getLoanOfficerDashboard: builder.query({
      query: () => ({
        url: `b2b/loan/dashboard/loan_officer`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data || null,
      providesTags: ['dashboardData'],
    }),
    // The closer's desk: what closes, what can be drawn, what is late.
    getCloserDashboard: builder.query({
      query: () => ({
        url: `b2b/loan/dashboard/closer`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data || null,
      providesTags: ['dashboardData'],
    }),
    // The organisation at a glance. The only role dashboard not scoped to the
    // signed-in user; a branch admin gets the same view, narrowed server side.
    getClientAdminDashboard: builder.query({
      query: () => ({
        url: `b2b/loan/dashboard/client_admin`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data || null,
      providesTags: ['dashboardData'],
    }),
  }),
});

export const {
  useAddLeadsMutation,
  useCreateConsumerApplicationMutation,
  useGetLoanOfficersQuery,
  useGetAllLeadsQuery,
  useLazyGetAllLeadsQuery,
  useLazyGetLeadDetailsQuery,
  useGetDashboardInformationQuery,
  useGetDashboardSummaryQuery,
  useGetDashboardNewLeadsQuery,
  useGetDashboardPipelineQuery,
  useGetDashboardLenderSpreadQuery,
  useGetProcessorDashboardQuery,
  useGetUnderwriterDashboardQuery,
  useGetCloserDashboardQuery,
  useGetLoanOfficerDashboardQuery,
  useGetClientAdminDashboardQuery,
} = loanInformationAPI;
